<template>
  <div
    v-show="!isMobile || !collapsed"
    class="base-sidebar-menu"
    :class="{
      'base-sidebar-menu--collapsed': railMode,
      'base-sidebar-menu--icons-only': iconsOnly,
      'base-sidebar-menu--mobile': isMobile,
    }"
    @transitionend="onWidthTransitionEnd"
  >
    <!-- Header / logo (links to the dashboard) -->
    <div class="base-sidebar-header">
      <router-link
        v-if="railMode"
        :to="homeLink"
        class="base-sidebar-logo-btn"
        aria-label="Go to dashboard"
        @click.native="onLogoClick"
      >
        <b-img
          width="32"
          height="32"
          class="base-sidebar-logo"
          :src="logoIcon"
        />
      </router-link>
      <template v-else>
        <router-link
          :to="homeLink"
          class="base-sidebar-logo-btn"
          aria-label="Go to dashboard"
          @click.native="onLogoClick"
        >
          <b-img
            class="base-sidebar-logo"
            height="32"
            :src="logo"
          />
        </router-link>
        <b-button
          class="font-w700 font-size-lg d-md-none"
          variant="link"
          aria-label="Close navigation menu"
          @click="closeMobile"
        >
          <i class="fa fa-xmark" />
        </b-button>
      </template>
    </div>
    <hr class="base-sidebar-divider">

    <!-- Menu -->
    <nav
      class="base-sidebar-nav"
      aria-label="Main Navigation"
    >
      <ul class="sb-list">
        <sidebar-item
          v-for="(item, index) in items"
          :key="index"
          :item="item"
          :collapsed="railMode"
          :depth="1"
          @item-click="handleItemClicked"
        />
      </ul>
    </nav>

    <!-- Collapse toggle (desktop only) -->
    <button
      v-if="!isMobile"
      type="button"
      class="base-sidebar-toggle sidebar-link-appearance"
      :aria-label="collapsed ? 'Expand sidebar' : 'Collapse sidebar'"
      :aria-expanded="collapsed ? 'false' : 'true'"
      @click="toggleCollapsed"
    >
      <i
        class="sb-icon fa"
        :class="collapsed ? 'fa-angles-right' : 'fa-angles-left'"
        aria-hidden="true"
      />
      <span
        v-if="!iconsOnly"
        class="base-sidebar-toggle__label"
      >
        Collapse sidebar
      </span>
    </button>
  </div>
</template>

<script>
import logo from '@/assets/images/sidebar-logo.png';
import logoIcon from '@/assets/images/sidebar-logo-icon.png';
import SidebarItem from '@/components/shared/SidebarItem.vue';

const MOBILE_BREAKPOINT = 768;

export default {
  name: 'BaseSidebarMenu',
  components: { SidebarItem },
  props: {
    items: {
      type: Array,
      required: true,
      description: 'The items for the menu',
    },
    collapsed: {
      type: Boolean,
      required: true,
      description: 'Whether the sidebar is collapsed to the icon rail',
    },
    homeLink: {
      type: [String, Object],
      default: '/',
      description: 'Route the logo links to (typically the dashboard)',
    },
  },
  data() {
    return {
      logo,
      logoIcon,
      isMobile: window.innerWidth < MOBILE_BREAKPOINT,
      // Whether labels are hidden (icon-only). Lags behind `railMode` when
      // expanding so labels only appear once the width transition finishes.
      iconsOnly: this.collapsed && window.innerWidth >= MOBILE_BREAKPOINT,
      widthTimer: null,
    };
  },
  created() {
    window.addEventListener('resize', this.updateIsMobile);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateIsMobile);
    this.clearWidthTimer();
  },
  computed: {
    // The icon-only rail only applies on desktop; on mobile the sidebar is
    // either a full-width drawer (expanded) or hidden (collapsed).
    railMode() {
      return this.collapsed && !this.isMobile;
    },
  },
  watch: {
    railMode(isRail) {
      this.clearWidthTimer();
      if (isRail) {
        // Collapsing: hide labels immediately so they don't overflow the
        // shrinking rail.
        this.iconsOnly = true;
      } else {
        // Expanding: reveal labels only after the width transition completes.
        // The transitionend handler normally fires first; this is a fallback.
        this.widthTimer = setTimeout(() => {
          this.iconsOnly = false;
        }, 350);
      }
    },
  },
  methods: {
    updateIsMobile() {
      this.isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    },
    onWidthTransitionEnd(event) {
      if (event.propertyName === 'width' && !this.railMode) {
        this.clearWidthTimer();
        this.iconsOnly = false;
      }
    },
    clearWidthTimer() {
      if (this.widthTimer) {
        clearTimeout(this.widthTimer);
        this.widthTimer = null;
      }
    },
    toggleCollapsed() {
      this.$emit('update:collapsed', !this.collapsed);
    },
    closeMobile() {
      this.$emit('update:collapsed', true);
    },
    onLogoClick() {
      // Navigating home from the mobile drawer should also close it.
      if (this.isMobile) {
        this.closeMobile();
      }
    },
    handleItemClicked(menuItem) {
      if (menuItem.action) {
        menuItem.action();
      }
      if (menuItem.modalId) {
        this.$bvModal.show(menuItem.modalId);
      }
      // On mobile, tapping a leaf navigates away, so close the drawer.
      if (this.isMobile && !menuItem.child?.length) {
        this.closeMobile();
      }
    },
  },
};
</script>
